import { useState, useEffect } from 'react';
import axios from 'axios';
import { Mail, Building2, CheckCircle, Clock, Send } from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';

const RappelEvaluationEntreprise = () => {
  const [entreprises, setEntreprises] = useState([]);
  const [rappels, setRappels] = useState([]);
  const [sending, setSending] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const [entRes, rappRes] = await Promise.all([
          axios.get('/entreprises'),
          axios.get('/satisfaction/rappels-evaluation').catch(() => ({ data: [] }))
        ]);
        setEntreprises(entRes.data || []);
        setRappels(rappRes.data || []);
      } catch (e) {
      }
      setLoading(false);
    };
    load();
  }, []);

  const handleSendRappel = async (entrepriseId, periode) => {
    const key = `${entrepriseId}-${periode}`;
    setSending(key);
    try {
      const res = await axios.post('/satisfaction/envoyer-rappel-evaluation', {
        entreprise_id: entrepriseId,
        periode
      });
      toast.success(res.data.message);
      const rappRes = await axios.get('/satisfaction/rappels-evaluation');
      setRappels(rappRes.data || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur lors de l\'envoi');
    }
    setSending(null);
  };

  const isDejaEnvoye = (entId, periode) => {
    return rappels.some(r => r.entreprise_id === entId && r.periode === periode);
  };

  if (loading || entreprises.length === 0) return null;

  const now = new Date();
  const month = now.getMonth(); // 0-indexed: 6 = July, 7 = August
  const showJuillet = month >= 5; // Show from June onwards
  const showAout = month >= 6; // Show from July onwards

  if (!showJuillet && !showAout) return null;

  return (
    <div className="bg-white border border-slate-200 rounded-xl p-6 shadow-sm" data-testid="rappel-evaluation-section">
      <div className="flex items-center gap-3 mb-4">
        <div className="bg-blue-100 p-3 rounded-lg">
          <Send className="h-6 w-6 text-blue-600" />
        </div>
        <div>
          <h2 className="text-xl font-display font-semibold text-[#0F172A]">Rappels Évaluation Entreprises</h2>
          <p className="text-sm text-slate-600">Envoi des bilans de fin juillet et fin août aux entreprises</p>
        </div>
      </div>

      <div className="space-y-3">
        {entreprises.map(ent => (
          <div key={ent.user_id} className="flex items-center justify-between p-3 bg-slate-50 border border-slate-200 rounded-lg" data-testid={`rappel-ent-${ent.user_id}`}>
            <div className="flex items-center gap-3">
              <Building2 className="h-5 w-5 text-slate-400" />
              <div>
                <p className="font-medium text-slate-800">{ent.nom || `${ent.prenom} ${ent.nom_famille || ''}`}</p>
                <p className="text-xs text-slate-500">{ent.email}</p>
              </div>
            </div>
            <div className="flex gap-2">
              {showJuillet && (
                <Button
                  size="sm"
                  variant={isDejaEnvoye(ent.user_id, 'fin juillet') ? 'outline' : 'default'}
                  className={`text-xs h-8 ${isDejaEnvoye(ent.user_id, 'fin juillet') ? 'border-green-300 text-green-600' : 'bg-blue-600 hover:bg-blue-700 text-white'}`}
                  disabled={sending === `${ent.user_id}-fin juillet`}
                  onClick={() => handleSendRappel(ent.user_id, 'fin juillet')}
                  data-testid={`rappel-juillet-${ent.user_id}`}
                >
                  {sending === `${ent.user_id}-fin juillet` ? (
                    <Clock className="h-3 w-3 animate-spin" />
                  ) : isDejaEnvoye(ent.user_id, 'fin juillet') ? (
                    <><CheckCircle className="h-3 w-3 mr-1" /> Juillet envoyé</>
                  ) : (
                    <><Mail className="h-3 w-3 mr-1" /> Bilan juillet</>
                  )}
                </Button>
              )}
              {showAout && (
                <Button
                  size="sm"
                  variant={isDejaEnvoye(ent.user_id, 'fin août') ? 'outline' : 'default'}
                  className={`text-xs h-8 ${isDejaEnvoye(ent.user_id, 'fin août') ? 'border-green-300 text-green-600' : 'bg-orange-500 hover:bg-orange-600 text-white'}`}
                  disabled={sending === `${ent.user_id}-fin août`}
                  onClick={() => handleSendRappel(ent.user_id, 'fin août')}
                  data-testid={`rappel-aout-${ent.user_id}`}
                >
                  {sending === `${ent.user_id}-fin août` ? (
                    <Clock className="h-3 w-3 animate-spin" />
                  ) : isDejaEnvoye(ent.user_id, 'fin août') ? (
                    <><CheckCircle className="h-3 w-3 mr-1" /> Août envoyé</>
                  ) : (
                    <><Mail className="h-3 w-3 mr-1" /> Bilan août</>
                  )}
                </Button>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default RappelEvaluationEntreprise;
